import { Text, Group } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useTenantMap } from '../data/useTenants';
import { useT } from '../i18n/I18nProvider';
import { CopyableId } from './CopyableId';

/**
 * The organisation behind a row, by name.
 *
 * Every cross-tenant page in this console holds an `organisation_id` and used
 * to print eight characters of the GUID, which is the one column an operator
 * reads by and the one they could not read. The name comes from the tenants
 * list already in the query cache; the id stays reachable in the tooltip and
 * behind a copy action, because support and engineering do quote it.
 *
 * `subtle` renders the name as muted body text, for places where the tenant is
 * context rather than the subject of the row.
 */
export function TenantName({ id, subtle = false, withCopy = false }: { id: string | null | undefined; subtle?: boolean; withCopy?: boolean }) {
  const t = useT();
  const { map, pending } = useTenantMap();

  if (!id) return <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.none')}</Text>;
  const tenant = map.get(id);

  // Before the tenants list lands, show nothing rather than a wrong "Unknown".
  if (!tenant) {
    return (
      <Tooltip label={pending ? t('common.loading') : t('tenantName.unknown', { id })} position="top">
        <Text role="bodySmall" c="var(--rr-color-text-muted)" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {pending ? '…' : t('tenantName.unknownShort')}
        </Text>
      </Tooltip>
    );
  }

  // The native title carries the full name for a truncated cell, which the
  // Text component does not forward, so it sits on the wrapper.
  const label = (
    <span title={tenant.name} style={{ minWidth: 0, display: 'block' }}>
      <Text
        role="bodySmall"
        fw={subtle ? 400 : 600}
        c={subtle ? 'var(--rr-color-text-muted)' : undefined}
        lineClamp={1}
      >
        {tenant.name}
      </Text>
    </span>
  );

  if (!withCopy) {
    return (
      <Tooltip label={t('tenantName.tooltip', { slug: tenant.slug })} position="top" openDelay={300}>
        <span style={{ display: 'inline-flex', minWidth: 0 }}>{label}</span>
      </Tooltip>
    );
  }

  return (
    <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
      <Tooltip label={t('tenantName.tooltip', { slug: tenant.slug })} position="top" openDelay={300}>
        <span style={{ display: 'inline-flex', minWidth: 0 }}>{label}</span>
      </Tooltip>
      <CopyableId value={id} label={t('tenantName.copyId')} />
    </Group>
  );
}
